import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
    ArrowLeft, Calendar, DollarSign, FileText, User, Clock, 
    Building2, CreditCard, History, AlertTriangle, Pencil, Trash2, GitBranch, Loader2, Mail, Send, Eye, CalendarClock,
    Banknote, RefreshCw, CircleCheck, CircleX, MessageSquareText, RotateCcw, Plus, Upload, Bell, MailCheck, MailOpen, MousePointerClick, MailX
} from 'lucide-react';
import { formatCurrency, formatDate, formatDateTime, getEstadoInfo, daysDiff } from '../lib/utils';
import EmailPreviewModal from '../components/EmailPreviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADOS = [
    { value: 'servicio_en_curso', label: 'En Curso' },
    { value: 'servicio_terminado', label: 'Terminado' },
    { value: 'edp_enviado', label: 'EDP Enviado' },
    { value: 'espera_oc', label: 'Espera OC' },
    { value: 'edp_aprobado', label: 'EDP Aprobado' },
    { value: 'edp_rechazado', label: 'EDP Rechazado' },
    { value: 'facturado', label: 'Facturado' },
    { value: 'pendiente_pago', label: 'Pendiente Pago' },
    { value: 'pago_parcial', label: 'Pago Parcial' },
    { value: 'pagado', label: 'Pagado' },
    { value: 'vencido', label: 'Vencido' },
    { value: 'en_mora', label: 'En Mora' },
    { value: 'cobranza_activa', label: 'Cobranza Activa' },
    { value: 'compromiso_pago', label: 'Compromiso Pago' },
    { value: 'servicio_interrumpido', label: 'Interrumpido' },
    { value: 'castigado', label: 'Castigado' }
];

const METODOS_PAGO = [
    { value: 'transferencia', label: 'Transferencia' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'tarjeta', label: 'Tarjeta' },
    { value: 'otro', label: 'Otro' }
];

// Reusable tracking badges for email envíos (EDP + recordatorios)
const EmailTrackingBadges = ({ tracking, testIdPrefix = 'tracking', showEmptyState = true }) => {
    if (!tracking) {
        return showEmptyState ? (
            <span
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-slate-100 text-slate-500 border border-slate-200"
                data-testid={`${testIdPrefix}-empty`}
            >
                <Mail className="w-3 h-3" /> Sin registro de envío
            </span>
        ) : null;
    }

    const hasAnyEvent = tracking.fecha_entregado || tracking.fecha_abierto || tracking.fecha_click ||
                        tracking.fecha_bounce || tracking.fecha_complaint;

    return (
        <div className="flex flex-wrap gap-1.5">
            {!hasAnyEvent && tracking.fecha_enviado && (
                <span
                    title={`Enviado ${formatDateTime(tracking.fecha_enviado)} — esperando confirmación`}
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-slate-50 text-slate-600 border border-slate-200"
                    data-testid={`${testIdPrefix}-sent`}
                >
                    <Send className="w-3 h-3" /> Enviado
                </span>
            )}
            {tracking.fecha_entregado && (
                <span
                    title={`Entregado ${formatDateTime(tracking.fecha_entregado)}`}
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                    data-testid={`${testIdPrefix}-delivered`}
                >
                    <MailCheck className="w-3 h-3" /> Entregado
                </span>
            )}
            {tracking.fecha_abierto && (
                <span
                    title={`Abierto ${tracking.veces_abierto || 1} vez(es). Última: ${formatDateTime(tracking.fecha_ultimo_abierto || tracking.fecha_abierto)}`}
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-sky-50 text-sky-700 border border-sky-200"
                    data-testid={`${testIdPrefix}-opened`}
                >
                    <MailOpen className="w-3 h-3" /> Abierto
                    {tracking.veces_abierto > 1 && (
                        <span className="ml-0.5 opacity-70">×{tracking.veces_abierto}</span>
                    )}
                </span>
            )}
            {tracking.fecha_click && (
                <span
                    title={`Click en ${tracking.ultimo_link_click || 'link'} — ${formatDateTime(tracking.fecha_click)}`}
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
                    data-testid={`${testIdPrefix}-clicked`}
                >
                    <MousePointerClick className="w-3 h-3" /> Click
                </span>
            )}
            {tracking.fecha_bounce && (
                <span
                    title={`Rebotado: ${tracking.bounce_motivo || 'sin motivo'}`}
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200"
                    data-testid={`${testIdPrefix}-bounced`}
                >
                    <MailX className="w-3 h-3" /> Rebotado
                </span>
            )}
            {tracking.fecha_complaint && (
                <span
                    title={`Marcado como spam: ${formatDateTime(tracking.fecha_complaint)}`}
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-orange-50 text-orange-700 border border-orange-200"
                    data-testid={`${testIdPrefix}-complaint`}
                >
                    <AlertTriangle className="w-3 h-3" /> Spam
                </span>
            )}
        </div>
    );
};

const ServicioDetalle = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canEdit } = useAuth();
    const [loading, setLoading] = useState(true);
    const [servicio, setServicio] = useState(null);
    const [cliente, setCliente] = useState(null);
    const [pagos, setPagos] = useState([]);
    const [historial, setHistorial] = useState([]);
    const [estadoDialogOpen, setEstadoDialogOpen] = useState(false);
    const [pagoDialogOpen, setPagoDialogOpen] = useState(false);
    const [facturarDialogOpen, setFacturarDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [aplicarFuturosDialogOpen, setAplicarFuturosDialogOpen] = useState(false);
    const [nuevoEstado, setNuevoEstado] = useState('');
    const [pagoForm, setPagoForm] = useState({
        fecha_pago: new Date().toISOString().split('T')[0],
        monto: '',
        metodo_pago: 'transferencia',
        observaciones: ''
    });
    const [facturaForm, setFacturaForm] = useState({
        numero_factura: '',
        fecha_facturacion: new Date().toISOString().split('T')[0]
    });
    const [editForm, setEditForm] = useState({
        nombre_producto: '',
        monto_neto: '',
        exento_iva: false,
        condicion_pago_dias: 30,
        fecha_inicio: '',
        fecha_termino: '',
        numero_factura: '',
        fecha_facturacion: '',
        observaciones: '',
        faena: '',
        ruta_cobro_id: '',
        requiere_edp: false
    });
    const [rutas, setRutas] = useState([]);
    const [editPagoForm, setEditPagoForm] = useState(null);
    const [editPagoDialogOpen, setEditPagoDialogOpen] = useState(false);
    const [deletePagoDialogOpen, setDeletePagoDialogOpen] = useState(false);
    const [recordatorioTracking, setRecordatorioTracking] = useState(null);

    // Modal de advertencia para número de factura duplicado
    // Estructura: { open, info, retryFn }
    const [duplicadoWarning, setDuplicadoWarning] = useState({ open: false, info: null, retryFn: null });

    // Toggle "Mostrar en línea de tiempo" — se sincroniza con servicio.mostrar_en_timeline
    const [toggleMostrarLoading, setToggleMostrarLoading] = useState(false);

    const handleToggleMostrarEnTimeline = async (nuevoValor) => {
        if (!servicio) return;
        setToggleMostrarLoading(true);
        try {
            const response = await axios.patch(
                `${API_URL}/api/servicios/${id}/mostrar-en-timeline`,
                { mostrar: nuevoValor }
            );
            const { servicios_actualizados, es_grupo } = response.data;
            if (nuevoValor) {
                toast.success(
                    es_grupo
                        ? `Marcado para aparecer en línea de tiempo (${servicios_actualizados} períodos del grupo)`
                        : 'Marcado para aparecer en línea de tiempo'
                );
            } else {
                toast.success(
                    es_grupo
                        ? `Removido del selector de línea de tiempo (${servicios_actualizados} períodos)`
                        : 'Removido del selector de línea de tiempo'
                );
            }
            fetchData();
        } catch (error) {
            console.error('Error toggling mostrar_en_timeline:', error);
            toast.error(error.response?.data?.detail || 'Error al actualizar');
        } finally {
            setToggleMostrarLoading(false);
        }
    };
    const [pagoToDelete, setPagoToDelete] = useState(null);
    const [pendingEditData, setPendingEditData] = useState(null);
    // Extension state
    const [extendMes, setExtendMes] = useState('');
    const [extendAnio, setExtendAnio] = useState('');
    const [extending, setExtending] = useState(false);
    
    // EDP states
    const [edps, setEdps] = useState([]);
    const [edpDialogOpen, setEdpDialogOpen] = useState(false);
    
    // Crear EDP states
    const [crearEdpOpen, setCrearEdpOpen] = useState(false);
    const [crearEdpLoading, setCrearEdpLoading] = useState(false);
    const [edpForm, setEdpForm] = useState({
        descripcion: '',
        faena: '',
        contacto_nombre: '',
        contacto_email: '',
        contacto_telefono: '',
        comentarios: '',
        edp_secuencia: '',
        monto_neto: '',
    });
    const [selectedEdp, setSelectedEdp] = useState(null);
    const [createEdpDialogOpen, setCreateEdpDialogOpen] = useState(false);
    const [nuevoEdpNumero, setNuevoEdpNumero] = useState('');
    const [deleteEdpDialogOpen, setDeleteEdpDialogOpen] = useState(false);

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [servicioRes, pagosRes, historialRes, edpsRes, recordatorioTrackingRes] = await Promise.all([
                axios.get(`${API_URL}/api/servicios/${id}`),
                axios.get(`${API_URL}/api/pagos?servicio_id=${id}`),
                axios.get(`${API_URL}/api/historial?servicio_id=${id}`),
                axios.get(`${API_URL}/api/edps?servicio_id=${id}`),
                axios.get(`${API_URL}/api/servicios/${id}/recordatorio-tracking`).catch(() => ({ data: { tracking: null } })),
            ]);
            setServicio(servicioRes.data);
            setPagos(pagosRes.data);
            setHistorial(historialRes.data);
            setEdps(edpsRes.data);
            setRecordatorioTracking(recordatorioTrackingRes.data?.tracking || null);

            // Fetch cliente for EDP contact pre-fill
            if (servicioRes.data?.cliente_id) {
                try {
                    const cliRes = await axios.get(`${API_URL}/api/clientes/${servicioRes.data.cliente_id}`);
                    setCliente(cliRes.data);
                } catch (_) { /* backend uses cliente as fallback */ }
            }

            // Fetch rutas de cobro for the edit form selector
            try {
                const rutasRes = await axios.get(`${API_URL}/api/rutas-cobro`);
                setRutas(rutasRes.data || []);
            } catch (_) { /* keep empty */ }
            
            // Initialize edit form with current values
            setEditForm({
                nombre_producto: servicioRes.data.nombre_producto || '',
                monto_neto: servicioRes.data.monto_neto || '',
                exento_iva: servicioRes.data.exento_iva || false,
                condicion_pago_dias: servicioRes.data.condicion_pago_dias || 30,
                fecha_inicio: servicioRes.data.fecha_inicio ? servicioRes.data.fecha_inicio.split('T')[0] : '',
                fecha_termino: servicioRes.data.fecha_termino ? servicioRes.data.fecha_termino.split('T')[0] : '',
                numero_factura: servicioRes.data.numero_factura || '',
                fecha_facturacion: servicioRes.data.fecha_facturacion ? servicioRes.data.fecha_facturacion.split('T')[0] : '',
                observaciones: servicioRes.data.observaciones || '',
                faena: servicioRes.data.faena || '',
                ruta_cobro_id: servicioRes.data.ruta_cobro_id || '',
                requiere_edp: servicioRes.data.requiere_edp || false
            });
        } catch (error) {
            console.error('Error fetching servicio:', error);
            toast.error('Error al cargar el servicio');
            navigate('/servicios');
        } finally {
            setLoading(false);
        }
    };

    const handleCambiarEstado = async () => {
        try {
            await axios.put(`${API_URL}/api/servicios/${id}`, {
                estado: nuevoEstado
            });
            toast.success('Estado actualizado');
            setEstadoDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error updating estado:', error);
            toast.error('Error al actualizar estado');
        }
    };

    const handleRegistrarPago = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/pagos`, {
                servicio_id: id,
                fecha_pago: new Date(pagoForm.fecha_pago).toISOString(),
                monto: parseFloat(pagoForm.monto),
                metodo_pago: pagoForm.metodo_pago,
                observaciones: pagoForm.observaciones
            });
            toast.success('Pago registrado');
            setPagoDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error registering pago:', error);
            toast.error('Error al registrar pago');
        }
    };

    // Helper: detect 409 duplicate_numero_factura response from backend
    const isDuplicadoFactura = (error) => {
        const d = error?.response?.data?.detail;
        return error?.response?.status === 409 && d && typeof d === 'object' && d.code === 'duplicate_numero_factura';
    };

    const handleFacturar = async (e) => {
        e.preventDefault();
        const nf = (facturaForm.numero_factura || '').trim();
        if (!nf) {
            toast.error('Debes ingresar un número de factura');
            return;
        }
        await submitFacturar(false);
    };

    const submitFacturar = async (forceDuplicado) => {
        const nf = (facturaForm.numero_factura || '').trim();
        try {
            const url = `${API_URL}/api/servicios/${id}${forceDuplicado ? '?force_duplicado=true' : ''}`;
            await axios.put(url, {
                numero_factura: nf,
                fecha_facturacion: `${facturaForm.fecha_facturacion}T12:00:00`,
                estado: 'facturado'
            });
            toast.success('Servicio facturado');
            setFacturarDialogOpen(false);
            setDuplicadoWarning({ open: false, info: null, retryFn: null });
            fetchData();
        } catch (error) {
            console.error('Error facturando:', error);
            if (isDuplicadoFactura(error)) {
                // Mostrar modal de advertencia
                setDuplicadoWarning({
                    open: true,
                    info: error.response.data.detail,
                    retryFn: () => submitFacturar(true),
                });
                return;
            }
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Error al facturar servicio');
        }
    };

    const handleExtenderMensual = async () => {
        // Reuse the current service's period as the source. Validate the target.
        if (!extendMes || !extendAnio) {
            toast.error('Selecciona mes y año hasta donde extender');
            return;
        }
        const targetMes = parseInt(extendMes);
        const targetAnio = parseInt(extendAnio);
        const srcKey = (servicio.periodo_anio || 0) * 100 + (servicio.periodo_mes || 0);
        if (targetAnio * 100 + targetMes <= srcKey) {
            toast.error('El período de destino debe ser posterior al período del servicio actual');
            return;
        }
        setExtending(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/servicios/${id}/extender`,
                { target_mes: targetMes, target_anio: targetAnio }
            );
            const { creados = 0, omitidos = 0 } = res.data || {};
            if (creados > 0) {
                toast.success(`Se crearon ${creados} períodos${omitidos > 0 ? ` (${omitidos} ya existían)` : ''}.`);
                setExtendMes('');
                setExtendAnio('');
                setEditDialogOpen(false);
                fetchData();
            } else {
                toast.info('No hay períodos nuevos por crear, todos ya existen.');
            }
        } catch (error) {
            console.error('Error extendiendo servicio:', error);
            toast.error(error.response?.data?.detail || 'No se pudo extender el servicio');
        } finally {
            setExtending(false);
        }
    };

    const handleEditSubmit = (e) => {
        e.preventDefault();
        const updateData = {
            nombre_producto: editForm.nombre_producto,
            monto_neto: parseFloat(editForm.monto_neto),
            exento_iva: editForm.exento_iva,
            condicion_pago_dias: parseInt(editForm.condicion_pago_dias),
            observaciones: editForm.observaciones,
            faena: editForm.faena,
            ruta_cobro_id: editForm.ruta_cobro_id || null,
            requiere_edp: editForm.requiere_edp
        };
        
        // Add optional fields if they have values
        if (editForm.fecha_inicio) {
            updateData.fecha_inicio = `${editForm.fecha_inicio}T12:00:00`;
        }
        if (editForm.fecha_termino) {
            updateData.fecha_termino = `${editForm.fecha_termino}T12:00:00`;
        }
        if (editForm.numero_factura) {
            updateData.numero_factura = editForm.numero_factura;
        }
        if (editForm.fecha_facturacion) {
            updateData.fecha_facturacion = `${editForm.fecha_facturacion}T12:00:00`;
        }
        
        // If service has a period (mes/año), always show the dialog
        if (servicio.periodo_mes && servicio.periodo_anio) {
            setPendingEditData(updateData);
            setEditDialogOpen(false);
            setAplicarFuturosDialogOpen(true);
        } else {
            // No period info, just update this service
            saveEdit(updateData, false);
        }
    };

    const saveEdit = async (updateData, aplicarAFuturos, forceDuplicado = false) => {
        try {
            const url = `${API_URL}/api/servicios/${id}${forceDuplicado ? '?force_duplicado=true' : ''}`;
            const response = await axios.put(url, {
                ...updateData,
                aplicar_a_futuros: aplicarAFuturos
            });
            
            const futurosActualizados = response.data.servicios_futuros_actualizados || 0;
            if (futurosActualizados > 0) {
                toast.success(`Servicio actualizado. También se actualizaron ${futurosActualizados} períodos futuros.`);
            } else {
                toast.success('Servicio actualizado');
            }
            
            setEditDialogOpen(false);
            setAplicarFuturosDialogOpen(false);
            setPendingEditData(null);
            setDuplicadoWarning({ open: false, info: null, retryFn: null });
            fetchData();
        } catch (error) {
            console.error('Error updating servicio:', error);
            if (isDuplicadoFactura(error)) {
                setDuplicadoWarning({
                    open: true,
                    info: error.response.data.detail,
                    retryFn: () => saveEdit(updateData, aplicarAFuturos, true),
                });
                return;
            }
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Error al actualizar servicio');
        }
    };

    const handleAplicarSoloEste = () => {
        if (pendingEditData) {
            saveEdit(pendingEditData, false);
        }
    };

    const handleAplicarConFuturos = () => {
        if (pendingEditData) {
            saveEdit(pendingEditData, true);
        }
    };

    const openEditDialog = () => {
        setEditForm({
            nombre_producto: servicio.nombre_producto || '',
            monto_neto: servicio.monto_neto || '',
            condicion_pago_dias: servicio.condicion_pago_dias || 30,
            fecha_inicio: servicio.fecha_inicio ? servicio.fecha_inicio.split('T')[0] : '',
            fecha_termino: servicio.fecha_termino ? servicio.fecha_termino.split('T')[0] : '',
            numero_factura: servicio.numero_factura || '',
            fecha_facturacion: servicio.fecha_facturacion ? servicio.fecha_facturacion.split('T')[0] : '',
            observaciones: servicio.observaciones || '',
            faena: servicio.faena || ''
        });
        setEditDialogOpen(true);
    };

    const openEditPagoDialog = (pago) => {
        setEditPagoForm({
            id: pago.id,
            fecha_pago: pago.fecha_pago ? pago.fecha_pago.split('T')[0] : '',
            monto: Math.round(pago.monto),
            metodo_pago: pago.metodo_pago,
            observaciones: pago.observaciones || ''
        });
        setEditPagoDialogOpen(true);
    };

    const handleEditPago = async (e) => {
        e.preventDefault();
        try {
            await axios.put(`${API_URL}/api/pagos/${editPagoForm.id}`, {
                fecha_pago: `${editPagoForm.fecha_pago}T12:00:00`,
                monto: editPagoForm.monto,
                metodo_pago: editPagoForm.metodo_pago,
                observaciones: editPagoForm.observaciones
            });
            toast.success('Pago actualizado');
            setEditPagoDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error updating pago:', error);
            toast.error('Error al actualizar pago');
        }
    };

    const handleDeletePago = async () => {
        if (!pagoToDelete) return;
        
        try {
            await axios.delete(`${API_URL}/api/pagos/${pagoToDelete.id}`);
            toast.success('Pago eliminado');
            setDeletePagoDialogOpen(false);
            setPagoToDelete(null);
            fetchData();
        } catch (error) {
            console.error('Error deleting pago:', error);
            toast.error('Error al eliminar pago');
        }
    };

    // EDP Functions
    const openEdpDialog = (edp) => {
        setSelectedEdp(edp);
        setEdpDialogOpen(true);
    };

    const handleUpdateEdpEstado = async (nuevoEstado) => {
        if (!selectedEdp) return;
        
        try {
            await axios.put(`${API_URL}/api/edps/${selectedEdp.id}`, {
                estado: nuevoEstado
            });
            toast.success(`EDP actualizado a: ${getEdpEstadoLabel(nuevoEstado)}`);
            setEdpDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error updating EDP:', error);
            toast.error('Error al actualizar EDP');
        }
    };

    const handleCrearEdp = async (e) => {
        e.preventDefault();
        setCrearEdpLoading(true);
        try {
            const montoNeto = parseFloat(edpForm.monto_neto || servicio?.monto_neto || 0);
            const response = await axios.post(`${API_URL}/api/edps/crear-con-pdf`, {
                servicio_id: id,
                descripcion: edpForm.descripcion || servicio?.nombre_producto,
                faena: edpForm.faena,
                contacto_nombre: edpForm.contacto_nombre,
                contacto_email: edpForm.contacto_email,
                contacto_telefono: edpForm.contacto_telefono,
                comentarios: edpForm.comentarios,
                edp_secuencia: edpForm.edp_secuencia,
                monto_neto: montoNeto,
            });
            toast.success(response.data.message);
            setCrearEdpOpen(false);
            
            // Open PDF in new tab
            if (response.data.pdf_url) {
                window.open(`${window.location.origin}${response.data.pdf_url}?t=${Date.now()}`, '_blank');
            }
            fetchData();
        } catch (error) {
            console.error('Error creating EDP:', error);
            toast.error(error.response?.data?.detail || 'Error al crear EDP');
        } finally {
            setCrearEdpLoading(false);
        }
    };

    // Estado para enviar EDP/recordatorio por email
    const [enviandoEdpId, setEnviandoEdpId] = useState(null);
    const [enviandoRecordatorio, setEnviandoRecordatorio] = useState(false);

    // Modal de preview de correo (EDP + recordatorio)
    const [emailPreviewCfg, setEmailPreviewCfg] = useState(null);
    // shape: { open, title, fetchPreview, sendEmail, allowSchedule, scheduleEmail, defaultTo, defaultCc, edp? }

    const openEdpEmailPreview = (edp) => {
        if (!edp) return;
        setEmailPreviewCfg({
            open: true,
            title: `Enviar EDP N° ${String(edp.numero_edp).padStart(4, '0')}`,
            defaultTo: cliente?.email_cobranza || edp.contacto_email || '',
            defaultCc: cliente?.email_cc || '',
            allowSchedule: true,
            fetchPreview: async ({ to, cc }) => {
                const res = await axios.post(
                    `${API_URL}/api/edps/${edp.id}/preview-email`,
                    { to: to || null, cc: cc ?? null },
                );
                return res.data;
            },
            sendEmail: async ({ to, cc, subject, body_html }) => {
                setEnviandoEdpId(edp.id);
                try {
                    const res = await axios.post(
                        `${API_URL}/api/edps/${edp.id}/enviar-email`,
                        { to: to || null, cc: cc ?? null, subject, body_html },
                    );
                    toast.success(`EDP enviado a ${res.data.to}`);
                    fetchData();
                } finally {
                    setEnviandoEdpId(null);
                }
            },
            scheduleEmail: async ({ to, cc, fecha_programada }) => {
                const res = await axios.post(
                    `${API_URL}/api/edps/${edp.id}/programar-envio`,
                    { to: to || null, cc: cc ?? null, fecha_programada },
                );
                const fp = res.data.envio_programado_at;
                toast.success(`Envío programado para ${new Date(fp).toLocaleString('es-CL')}`);
                fetchData();
            },
        });
    };

    const openRecordatorioEmailPreview = () => {
        if (!servicio) return;
        setEmailPreviewCfg({
            open: true,
            title: 'Enviar recordatorio de pago',
            defaultTo: cliente?.email_cobranza || '',
            defaultCc: cliente?.email_cc || '',
            allowSchedule: false,
            fetchPreview: async ({ to, cc }) => {
                const res = await axios.post(
                    `${API_URL}/api/servicios/${servicio.id}/preview-recordatorio-email`,
                    { to: to || null, cc: cc ?? null },
                );
                return res.data;
            },
            sendEmail: async ({ to, cc, subject, body_html }) => {
                setEnviandoRecordatorio(true);
                try {
                    const res = await axios.post(
                        `${API_URL}/api/servicios/${servicio.id}/enviar-recordatorio-pago`,
                        { to: to || null, cc: cc ?? null, subject, body_html },
                    );
                    toast.success(`Recordatorio enviado a ${res.data.to}`);
                    fetchData();
                } finally {
                    setEnviandoRecordatorio(false);
                }
            },
        });
    };

    // Keep the old handlers as aliases (in case other code refers to them)
    const handleEnviarEdpEmail = openEdpEmailPreview;
    const handleEnviarRecordatorioPago = openRecordatorioEmailPreview;

    // Cancel scheduled EDP send
    const handleCancelarProgramacion = async (edp) => {
        if (!edp) return;
        try {
            await axios.delete(`${API_URL}/api/edps/${edp.id}/programar-envio`);
            toast.success('Envío programado cancelado');
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al cancelar programación');
        }
    };

    // View EDP PDF in a new tab
    const handleVerEdp = async (edp) => {
        if (edp.pdf_url) {
            window.open(`${API_URL}${edp.pdf_url}`, '_blank');
            return;
        }
        try {
            const res = await axios.post(`${API_URL}/api/edps/generar-pdf/${edp.id}`);
            if (res.data?.pdf_url) {
                window.open(`${API_URL}${res.data.pdf_url}`, '_blank');
            } else {
                toast.error('No se pudo generar el PDF del EDP');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al abrir el PDF');
        }
    };


    const getEdpEstadoLabel = (estado) => {
        const labels = {
            'sin_edp': 'Sin EDP',
            'enviado': 'Enviado',
            'rechazado': 'Rechazado',
            'aprobado': 'Aprobado'
        };
        return labels[estado] || estado;
    };

    const getEdpEstadoBadge = (estado) => {
        const styles = {
            'sin_edp': 'bg-slate-100 text-slate-700 border-slate-300',
            'enviado': 'bg-[#D5E8E8] text-[#3D7A7A] border-[#8BC5C5]',
            'rechazado': 'bg-rose-100 text-rose-700 border-rose-300',
            'aprobado': 'bg-emerald-100 text-emerald-700 border-emerald-300'
        };
        return styles[estado] || 'bg-slate-100 text-slate-700 border-slate-300';
    };

    const openCreateEdpDialog = () => {
        setNuevoEdpNumero('');
        setCreateEdpDialogOpen(true);
    };

    const handleCreateEdp = async () => {
        if (!servicio?.requiere_edp || !nuevoEdpNumero) {
            toast.error('Debe ingresar el número de EDP');
            return;
        }
        
        try {
            await axios.post(`${API_URL}/api/edps`, {
                servicio_id: servicio.id,
                numero_edp: parseInt(nuevoEdpNumero),
                periodo_mes: servicio.periodo_mes,
                periodo_anio: servicio.periodo_anio
            });
            toast.success('EDP creado');
            setCreateEdpDialogOpen(false);
            setNuevoEdpNumero('');
            fetchData();
        } catch (error) {
            if (error.response?.data?.detail?.includes('Ya existe')) {
                toast.error('Ya existe un EDP para este período');
            } else {
                console.error('Error creating EDP:', error);
                toast.error('Error al crear EDP');
            }
        }
    };

    const handleDeleteEdp = async () => {
        if (!selectedEdp) return;
        
        try {
            await axios.delete(`${API_URL}/api/edps/${selectedEdp.id}`);
            toast.success('EDP eliminado');
            setDeleteEdpDialogOpen(false);
            setEdpDialogOpen(false);
            setSelectedEdp(null);
            fetchData();
        } catch (error) {
            console.error('Error deleting EDP:', error);
            toast.error('Error al eliminar EDP');
        }
    };

    const openDeletePagoDialog = (pago) => {
        setPagoToDelete(pago);
        setDeletePagoDialogOpen(true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9A9A]"></div>
            </div>
        );
    }

    if (!servicio) return null;

    const estadoInfo = getEstadoInfo(servicio.estado);
    const diasVencido = servicio.fecha_vencimiento ? daysDiff(servicio.fecha_vencimiento) : 0;
    const saldoRedondeado = Math.round(parseFloat(servicio.saldo_pendiente) || 0);
    const estaVencido = diasVencido > 0 && saldoRedondeado > 0 && servicio.estado !== 'pagado';

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/servicios')}>
                    <ArrowLeft size={20} />
                </Button>
                <div className="flex-1">
                    <h1 className="text-2xl font-bold text-slate-900">
                        {servicio.nombre_producto || servicio.descripcion}
                        {servicio.faena && (
                            <>
                                <span className="text-slate-400 font-normal mx-2">|</span>
                                <span className="font-medium text-slate-700">{servicio.faena}</span>
                            </>
                        )}
                        <span className="text-slate-400 font-normal mx-2">|</span>
                        <span className="font-medium text-slate-600">{servicio.cliente_nombre}</span>
                    </h1>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${estadoInfo.color}`}>
                    {estadoInfo.label}
                </span>
            </div>

            {/* Alert if overdue */}
            {estaVencido && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-center gap-3 mt-4">
                    <AlertTriangle className="text-rose-600" size={24} />
                    <div>
                        <p className="font-semibold text-rose-800">Servicio vencido hace {diasVencido} días</p>
                        <p className="text-sm text-rose-600">Saldo pendiente: {formatCurrency(servicio.saldo_pendiente)}</p>
                    </div>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#D5E8E8] rounded-lg flex items-center justify-center">
                            <DollarSign className="text-[#5B9A9A]" size={20} />
                        </div>
                        <div>
                            <p className="text-xl font-bold">{formatCurrency(servicio.monto_total)}</p>
                            <p className="text-xs text-slate-500">Total</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <DollarSign className="text-emerald-600" size={20} />
                        </div>
                        <div>
                            <p className="text-xl font-bold">{formatCurrency(servicio.monto_total - (servicio.saldo_pendiente || 0))}</p>
                            <p className="text-xs text-slate-500">Pagado</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className={`w-10 h-10 ${estaVencido ? 'bg-rose-100' : 'bg-amber-100'} rounded-lg flex items-center justify-center`}>
                            <DollarSign className={estaVencido ? 'text-rose-600' : 'text-amber-600'} size={20} />
                        </div>
                        <div>
                            <p className={`text-xl font-bold ${estaVencido ? 'text-rose-600' : ''}`}>
                                {formatCurrency(servicio.saldo_pendiente || 0)}
                            </p>
                            <p className="text-xs text-slate-500">Pendiente</p>
                        </div>
                    </CardContent>
                </Card>
                {/* Período Card */}
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <Calendar className="text-indigo-600" size={20} />
                        </div>
                        <div>
                            <p className="text-xl font-bold">
                                {servicio.periodo_mes && servicio.periodo_anio 
                                    ? `${servicio.periodo_mes}/${servicio.periodo_anio}`
                                    : '-'}
                            </p>
                            <p className="text-xs text-slate-500">Período</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Clock className="text-slate-600" size={20} />
                        </div>
                        <div>
                            <p className="text-xl font-bold">{formatDate(servicio.fecha_vencimiento)}</p>
                            <p className="text-xs text-slate-500">Vencimiento</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Actions */}
            {canEdit() && (
                <div className="flex flex-wrap gap-2 pt-2">
                    <Button 
                        variant="outline" 
                        onClick={openEditDialog}
                        className="border-[#8BC5C5] text-[#3D7A7A] hover:bg-[#E8F4F4] hover:border-[#6BA5A5]"
                    >
                        <Pencil size={16} className="mr-2" />
                        Editar Servicio
                    </Button>
                    <Button 
                        variant="outline"
                        onClick={() => navigate(`/servicios/${id}/timeline`)}
                        className="border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400"
                    >
                        <GitBranch size={16} className="mr-2" />
                        Línea de Tiempo
                    </Button>
                    <Button 
                        onClick={() => {
                            setNuevoEstado(servicio.estado);
                            setEstadoDialogOpen(true);
                        }}
                        className="bg-rose-600 hover:bg-rose-700"
                    >
                        Cambiar Estado
                    </Button>
                    {!servicio.fecha_facturacion && (
                        <Button 
                            variant="outline" 
                            onClick={() => setFacturarDialogOpen(true)}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400"
                        >
                            <FileText size={16} className="mr-2" />
                            Facturar
                        </Button>
                    )}
                    {servicio.saldo_pendiente > 0 && (
                        <Button 
                            onClick={() => {
                                setPagoForm({
                                    fecha_pago: new Date().toISOString().split('T')[0],
                                    monto: Math.round(servicio.saldo_pendiente),
                                    metodo_pago: 'transferencia',
                                    observaciones: ''
                                });
                                setPagoDialogOpen(true);
                            }}
                            className="bg-emerald-600 hover:bg-emerald-700"
                        >
                            <CreditCard size={16} className="mr-2" />
                            Registrar Pago
                        </Button>
                    )}
                </div>
            )}

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Service Info */}
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <CardTitle className="text-base">Información del Servicio</CardTitle>
                            {canEdit() && (
                                <div className="flex items-center gap-2.5">
                                    <div className="flex flex-col items-end">
                                        <Label
                                            htmlFor="toggle-mostrar-timeline"
                                            className="text-xs font-medium text-slate-700 cursor-pointer leading-tight"
                                        >
                                            Mostrar en Línea de Tiempo
                                        </Label>
                                        {servicio?.grupo_recurrente_id && (
                                            <span className="text-[10px] text-slate-400 leading-tight mt-0.5">
                                                Aplica a todo el grupo recurrente
                                            </span>
                                        )}
                                    </div>
                                    <Switch
                                        id="toggle-mostrar-timeline"
                                        checked={!!servicio?.mostrar_en_timeline}
                                        disabled={toggleMostrarLoading}
                                        onCheckedChange={handleToggleMostrarEnTimeline}
                                        className="data-[state=checked]:bg-teal-600"
                                    />
                                </div>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-slate-500">Monto Neto</p>
                                <p className="font-medium">{formatCurrency(servicio.monto_neto)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">IVA (19%)</p>
                                {servicio.exento_iva ? (
                                    <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">Exento</Badge>
                                ) : (
                                    <p className="font-medium">{formatCurrency(servicio.iva)}</p>
                                )}
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Fecha Inicio</p>
                                <p className="font-medium">{formatDate(servicio.fecha_inicio)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Fecha Término</p>
                                <p className="font-medium">{formatDate(servicio.fecha_termino) || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Condición de Pago</p>
                                <p className="font-medium">{servicio.condicion_pago_dias} días</p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Responsable</p>
                                <p className="font-medium">{servicio.responsable_nombre || '-'}</p>
                            </div>
                        </div>

                        {servicio.fecha_facturacion && (
                            <div className="pt-4 border-t">
                                <p className="text-xs text-slate-500 mb-2">Facturación</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-slate-500">Número Factura</p>
                                        <p className="font-medium">{servicio.numero_factura}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500">Fecha Facturación</p>
                                        <p className="font-medium">{formatDate(servicio.fecha_facturacion)}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* EDP Section */}
                        {servicio.requiere_edp && (() => {
                            const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
                            const mesesLargo = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
                            const currentEdps = edps.filter(e =>
                                e.periodo_mes === servicio.periodo_mes && e.periodo_anio === servicio.periodo_anio
                            );
                            const renderEdpCard = (edp, isOtherPeriod = false) => (
                                <div
                                    key={edp.id}
                                    className={`${isOtherPeriod ? 'bg-white border-slate-200' : 'bg-slate-50'} border rounded-lg p-3 cursor-pointer hover:bg-slate-100 transition-colors`}
                                    onClick={() => openEdpDialog(edp)}
                                    data-testid={`edp-card-${edp.id}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-semibold text-sm">
                                                EDP Nº {String(edp.numero_edp).padStart(4, '0')}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                                Período: {meses[(edp.periodo_mes || 1) - 1]} {edp.periodo_anio}
                                                {isOtherPeriod && <span className="ml-1 text-slate-400">· otro período</span>}
                                            </p>
                                        </div>
                                        <Badge
                                            variant="outline"
                                            className={`text-xs ${getEdpEstadoBadge(edp.estado)}`}
                                        >
                                            {getEdpEstadoLabel(edp.estado)}
                                        </Badge>
                                    </div>
                                    {edp.fecha_envio && (
                                        <p className="text-xs text-slate-500 mt-1">
                                            Enviado: {formatDate(edp.fecha_envio)}
                                        </p>
                                    )}
                                    <div className="mt-1.5">
                                        <EmailTrackingBadges
                                            tracking={edp.tracking}
                                            testIdPrefix={`edp-${edp.id}-tracking`}
                                            showEmptyState={!edp.fecha_envio}
                                        />
                                    </div>
                                    {edp.numero_oc && (
                                        <p className="text-xs text-emerald-600 mt-1">
                                            OC: {edp.numero_oc}
                                        </p>
                                    )}
                                    {(edp.aprobacion_por_nombre || edp.aprobacion_comentario) && (
                                        <div className={`mt-2 p-2 rounded border-l-2 text-xs ${edp.estado === 'aprobado' ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-400'}`}>
                                            <p className={`font-semibold ${edp.estado === 'aprobado' ? 'text-emerald-800' : 'text-red-800'}`}>
                                                {edp.estado === 'aprobado' ? 'Aprobado' : 'Rechazado'} por el cliente
                                                {edp.aprobacion_por_nombre ? ` (${edp.aprobacion_por_nombre})` : ''}
                                            </p>
                                            {edp.fecha_respuesta && (
                                                <p className="text-slate-600 mt-0.5">
                                                    {new Date(edp.fecha_respuesta).toLocaleString('es-CL', { timeZone: 'America/Santiago', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            )}
                                            {edp.aprobacion_comentario && (
                                                <p className="text-slate-700 mt-1 italic">"{edp.aprobacion_comentario}"</p>
                                            )}
                                        </div>
                                    )}
                                    {edp.envio_programado_at && edp.envio_programado_estado === 'programado' ? (
                                        <div className="mt-2 p-2 rounded border-l-2 bg-amber-50 border-amber-400 text-xs">
                                            <p className="font-semibold text-amber-800 flex items-center gap-1">
                                                <CalendarClock className="w-3 h-3" />
                                                Envío automático programado
                                            </p>
                                            <p className="text-amber-700 mt-0.5">
                                                {new Date(edp.envio_programado_at).toLocaleString('es-CL', { timeZone: 'America/Santiago', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                {edp.envio_programado_to ? ` → ${edp.envio_programado_to}` : ''}
                                            </p>
                                        </div>
                                    ) : (edp.estado !== 'enviado' && edp.estado !== 'aprobado') && (() => {
                                        const target = new Date(edp.periodo_anio, (edp.periodo_mes || 1) - 1, 25, 9, 0);
                                        const alreadyPast = target.getTime() <= Date.now();
                                        return (
                                            <div className="mt-2 p-2 rounded border-l-2 bg-slate-50 border-slate-300 text-xs">
                                                <p className="font-semibold text-slate-700 flex items-center gap-1">
                                                    <CalendarClock className="w-3 h-3" />
                                                    Envío automático
                                                </p>
                                                <p className="text-slate-600 mt-0.5">
                                                    {`25 ${mesesLargo[(edp.periodo_mes || 1) - 1]} ${edp.periodo_anio}, 09:00 (hora Chile)`}
                                                    {alreadyPast && <span className="ml-1 text-slate-400 italic">— fecha ya pasada, envía manualmente</span>}
                                                </p>
                                            </div>
                                        );
                                    })()}
                                    {edp.envio_programado_estado === 'fallido' && (
                                        <div className="mt-2 p-2 rounded border-l-2 bg-red-50 border-red-400 text-xs">
                                            <p className="font-semibold text-red-800">Envío programado falló</p>
                                            <p className="text-red-700 mt-0.5">{edp.envio_programado_error || 'Error desconocido'}</p>
                                        </div>
                                    )}
                                    {canEdit && edp.estado !== 'aprobado' && (
                                        <div className="mt-2 flex flex-wrap justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-xs border-slate-300 text-slate-700 hover:bg-slate-100"
                                                data-testid={`ver-edp-btn-${edp.id}`}
                                                onClick={(e) => { e.stopPropagation(); handleVerEdp(edp); }}
                                            >
                                                <Eye className="w-3 h-3 mr-1" />
                                                Ver EDP
                                            </Button>
                                            {edp.envio_programado_estado === 'programado' && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                                                    data-testid={`cancelar-programacion-btn-${edp.id}`}
                                                    onClick={(e) => { e.stopPropagation(); handleCancelarProgramacion(edp); }}
                                                >
                                                    <CalendarClock className="w-3 h-3 mr-1" />
                                                    Cancelar programación
                                                </Button>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
                                                disabled={enviandoEdpId === edp.id}
                                                data-testid={`enviar-edp-btn-${edp.id}`}
                                                onClick={(e) => { e.stopPropagation(); handleEnviarEdpEmail(edp); }}
                                            >
                                                {enviandoEdpId === edp.id ? (
                                                    <>
                                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                        Enviando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Mail className="w-3 h-3 mr-1" />
                                                        {edp.estado === 'enviado' ? 'Reenviar por email' : 'Enviar por email'}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            );
                            return (
                            <div className="pt-4 border-t">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs text-slate-500">EDP (Estado de Pago)</p>
                                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-300">
                                        Requiere EDP
                                    </Badge>
                                </div>
                                {currentEdps.length > 0 ? (
                                    <div className="space-y-2">
                                        {currentEdps.map((edp) => renderEdpCard(edp, false))}
                                    </div>
                                ) : (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                        <p className="text-amber-700 font-medium text-sm mb-2">Sin EDP creado para este período</p>
                                        <Button 
                                            size="sm" 
                                            variant="outline" 
                                            onClick={openCreateEdpDialog}
                                            className="text-amber-700 border-amber-300 hover:bg-amber-100"
                                        >
                                            Crear EDP
                                        </Button>
                                    </div>
                                )}
                            </div>
                            );
                        })()}

                        {/* Fecha de Pago */}
                        <div className="pt-4 border-t">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-xs text-slate-500">Pago</p>
                                {canEdit() && servicio && parseFloat(servicio.saldo_pendiente || 0) > 0 && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
                                        disabled={enviandoRecordatorio}
                                        onClick={handleEnviarRecordatorioPago}
                                    >
                                        {enviandoRecordatorio ? (
                                            <>
                                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                Enviando...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-3 h-3 mr-1" />
                                                Enviar Recordatorio
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                            {/* Tracking del último recordatorio enviado */}
                            <div className="mb-3">
                                <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Último recordatorio</p>
                                <EmailTrackingBadges
                                    tracking={recordatorioTracking}
                                    testIdPrefix="recordatorio-tracking"
                                    showEmptyState={true}
                                />
                                {recordatorioTracking?.fecha_enviado && (
                                    <p className="text-[11px] text-slate-500 mt-1">
                                        Enviado a {recordatorioTracking.to || '—'} el {formatDateTime(recordatorioTracking.fecha_enviado)}
                                    </p>
                                )}
                            </div>
                            {pagos.length > 0 ? (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-slate-500">Fecha de Pago</p>
                                        <p className="font-medium">{formatDate(pagos[0].fecha_pago)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500">Método</p>
                                        <p className="font-medium capitalize">{pagos[0].metodo_pago}</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                                    <p className="text-rose-700 font-medium text-sm">Sin registro de pago</p>
                                </div>
                            )}
                        </div>

                        {servicio.faena && (
                            <div className="pt-4 border-t">
                                <p className="text-xs text-slate-500">Faena / Ubicación</p>
                                <p className="text-sm mt-1">{servicio.faena}</p>
                            </div>
                        )}

                        {servicio.observaciones && (
                            <div className="pt-4 border-t">
                                <p className="text-xs text-slate-500">Observaciones</p>
                                <p className="text-sm mt-1">{servicio.observaciones}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Pagos */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Pagos Registrados</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {pagos.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-4">Sin pagos registrados</p>
                        ) : (
                            <div className="space-y-3">
                                {pagos.map((pago) => (
                                    <div key={pago.id} className="p-3 bg-slate-50 rounded-lg">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="font-semibold text-emerald-600">
                                                    {formatCurrency(pago.monto)}
                                                </p>
                                                <p className="text-xs text-slate-500">{formatDate(pago.fecha_pago)}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="text-xs">
                                                    {pago.metodo_pago}
                                                </Badge>
                                                {canEdit() && (
                                                    <>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon" 
                                                            className="h-6 w-6"
                                                            onClick={() => openEditPagoDialog(pago)}
                                                        >
                                                            <Pencil size={12} />
                                                        </Button>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon" 
                                                            className="h-6 w-6 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                                                            onClick={() => openDeletePagoDialog(pago)}
                                                            data-testid={`delete-pago-btn-${pago.id}`}
                                                        >
                                                            <Trash2 size={12} />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {pago.es_pago_parcial && (
                                            <p className="text-xs text-amber-600 mt-1">Pago parcial</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Historial */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <History size={18} />
                        Historial
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {historial.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">Sin registros en historial</p>
                    ) : (
                        <div className="space-y-3">
                            {historial.map((item) => {
                                const acc = (item.accion || '').toLowerCase();
                                let Icon = FileText;
                                let iconColor = 'text-slate-500';
                                let bg = 'bg-slate-100';
                                if (acc.includes('cambio de estado') || acc.startsWith('estado:')) {
                                    Icon = RefreshCw; iconColor = 'text-indigo-600'; bg = 'bg-indigo-50';
                                } else if (acc.includes('cobro') || acc.includes('pago registrado') || acc.includes('registró pago')) {
                                    Icon = Banknote; iconColor = 'text-emerald-600'; bg = 'bg-emerald-50';
                                } else if (acc.includes('enviado por email') || acc.includes('envío automático de edp')) {
                                    Icon = Send; iconColor = 'text-teal-700'; bg = 'bg-teal-50';
                                } else if (acc.includes('recordatorio')) {
                                    Icon = Bell; iconColor = 'text-amber-600'; bg = 'bg-amber-50';
                                } else if (acc.includes('aprobado') || acc.includes('aprobó')) {
                                    Icon = CircleCheck; iconColor = 'text-emerald-600'; bg = 'bg-emerald-50';
                                } else if (acc.includes('rechazado') || acc.includes('rechazó')) {
                                    Icon = CircleX; iconColor = 'text-red-600'; bg = 'bg-red-50';
                                } else if (acc.includes('comentario') || acc.includes('observación') || acc.includes('nota')) {
                                    Icon = MessageSquareText; iconColor = 'text-slate-500'; bg = 'bg-slate-100';
                                } else if (acc.includes('actualiz') || acc.includes('modific') || acc.includes('editó') || acc.includes('cambió')) {
                                    Icon = Pencil; iconColor = 'text-slate-500'; bg = 'bg-slate-100';
                                } else if (acc.includes('creó') || acc.includes('creación') || acc.includes('nuevo servicio') || acc.includes('nueva factura')) {
                                    Icon = Plus; iconColor = 'text-cyan-600'; bg = 'bg-cyan-50';
                                } else if (acc.includes('facturado') || acc.includes('factura')) {
                                    Icon = FileText; iconColor = 'text-purple-600'; bg = 'bg-purple-50';
                                } else if (acc.includes('reversión') || acc.includes('anul')) {
                                    Icon = RotateCcw; iconColor = 'text-red-600'; bg = 'bg-red-50';
                                } else if (acc.includes('carga') || acc.includes('import')) {
                                    Icon = Upload; iconColor = 'text-slate-500'; bg = 'bg-slate-100';
                                } else if (acc.includes('programad')) {
                                    Icon = CalendarClock; iconColor = 'text-amber-600'; bg = 'bg-amber-50';
                                }
                                return (
                                    <div key={item.id} className="flex items-start gap-3 p-3 border-l-2 border-slate-200" data-testid={`historial-item-${item.id}`}>
                                        <div className={`shrink-0 w-8 h-8 rounded-full ${bg} flex items-center justify-center`}>
                                            <Icon className={`w-4 h-4 ${iconColor}`} />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">{item.accion}</p>
                                            <p className="text-xs text-slate-500">
                                                {item.usuario_nombre || 'Sistema'} • {formatDateTime(item.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Dialogs */}
            {/* Cambiar Estado Dialog */}
            <Dialog open={estadoDialogOpen} onOpenChange={setEstadoDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cambiar Estado</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Label>Nuevo Estado</Label>
                        <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                            <SelectTrigger className="mt-2">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ESTADOS.map((estado) => (
                                    <SelectItem key={estado.value} value={estado.value}>
                                        {estado.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEstadoDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleCambiarEstado}>
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Registrar Pago Dialog */}
            <Dialog open={pagoDialogOpen} onOpenChange={setPagoDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Registrar Pago</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleRegistrarPago}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>Fecha de Pago</Label>
                                <Input
                                    type="date"
                                    value={pagoForm.fecha_pago}
                                    onChange={(e) => setPagoForm({...pagoForm, fecha_pago: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Monto</Label>
                                <Input
                                    type="text"
                                    value={pagoForm.monto ? `$${Math.round(pagoForm.monto).toLocaleString('es-CL')}` : ''}
                                    onChange={(e) => {
                                        // Remove all non-numeric characters
                                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                        setPagoForm({...pagoForm, monto: parseInt(numericValue) || 0});
                                    }}
                                    required
                                    placeholder="$0"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Método de Pago</Label>
                                <Select 
                                    value={pagoForm.metodo_pago} 
                                    onValueChange={(v) => setPagoForm({...pagoForm, metodo_pago: v})}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {METODOS_PAGO.map((m) => (
                                            <SelectItem key={m.value} value={m.value}>
                                                {m.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Observaciones</Label>
                                <Textarea
                                    value={pagoForm.observaciones}
                                    onChange={(e) => setPagoForm({...pagoForm, observaciones: e.target.value})}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setPagoDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit">Registrar Pago</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Facturar Dialog */}
            <Dialog open={facturarDialogOpen} onOpenChange={setFacturarDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Facturar Servicio</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleFacturar}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>Número de Factura</Label>
                                <Input
                                    value={facturaForm.numero_factura}
                                    onChange={(e) => setFacturaForm({...facturaForm, numero_factura: e.target.value})}
                                    required
                                    placeholder="F-001"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Fecha de Facturación</Label>
                                <Input
                                    type="date"
                                    value={facturaForm.fecha_facturacion}
                                    onChange={(e) => setFacturaForm({...facturaForm, fecha_facturacion: e.target.value})}
                                    required
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setFacturarDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit">Facturar</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Editar Servicio Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Editar Servicio</DialogTitle>
                        {servicio?.periodo_mes && servicio?.periodo_anio && (
                            <DialogDescription>
                                Período: {servicio.periodo_mes}/{servicio.periodo_anio}
                            </DialogDescription>
                        )}
                    </DialogHeader>
                    <form onSubmit={handleEditSubmit}>
                        <div className="space-y-4 py-4">
                            {/* Basic Info */}
                            <div className="space-y-2">
                                <Label>Nombre del Producto/Servicio</Label>
                                <Input
                                    value={editForm.nombre_producto}
                                    onChange={(e) => setEditForm({...editForm, nombre_producto: e.target.value})}
                                    placeholder="Ej: Servicio de Consultoría"
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Monto Neto</Label>
                                    <Input
                                        type="text"
                                        value={editForm.monto_neto ? `$${Math.round(editForm.monto_neto).toLocaleString('es-CL')}` : ''}
                                        onChange={(e) => {
                                            const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                            setEditForm({...editForm, monto_neto: parseInt(numericValue) || 0});
                                        }}
                                        required
                                        placeholder="$0"
                                    />
                                    <div className="flex items-center space-x-2 mt-2">
                                        <Checkbox 
                                            id="edit_exento_iva"
                                            checked={editForm.exento_iva}
                                            onCheckedChange={(checked) => setEditForm({...editForm, exento_iva: checked})}
                                        />
                                        <Label 
                                            htmlFor="edit_exento_iva" 
                                            className="text-sm font-normal cursor-pointer text-slate-600"
                                        >
                                            Exento de IVA
                                        </Label>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Días de Pago</Label>
                                    <Input
                                        type="number"
                                        value={editForm.condicion_pago_dias}
                                        onChange={(e) => setEditForm({...editForm, condicion_pago_dias: e.target.value})}
                                        required
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Fecha Inicio</Label>
                                    <Input
                                        type="date"
                                        value={editForm.fecha_inicio}
                                        onChange={(e) => setEditForm({...editForm, fecha_inicio: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Fecha Término</Label>
                                    <Input
                                        type="date"
                                        value={editForm.fecha_termino}
                                        onChange={(e) => setEditForm({...editForm, fecha_termino: e.target.value})}
                                    />
                                </div>
                            </div>

                            {/* Facturación */}
                            <div className="pt-4 border-t">
                                <p className="text-sm font-medium text-slate-700 mb-3">Facturación</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Número Factura</Label>
                                        <Input
                                            value={editForm.numero_factura}
                                            onChange={(e) => setEditForm({...editForm, numero_factura: e.target.value})}
                                            placeholder="F-001"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Fecha Facturación</Label>
                                        <Input
                                            type="date"
                                            value={editForm.fecha_facturacion}
                                            onChange={(e) => setEditForm({...editForm, fecha_facturacion: e.target.value})}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Faena / Ubicación</Label>
                                <Input
                                    value={editForm.faena}
                                    onChange={(e) => setEditForm({...editForm, faena: e.target.value})}
                                    placeholder="Ej: Oficina Central Santiago"
                                    data-testid="edit-servicio-faena"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Observaciones</Label>
                                <Textarea
                                    value={editForm.observaciones}
                                    onChange={(e) => setEditForm({...editForm, observaciones: e.target.value})}
                                    rows={2}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Ruta de Cobro <span className="text-xs text-slate-400 font-normal">(opcional)</span></Label>
                                <Select
                                    value={editForm.ruta_cobro_id || 'none'}
                                    onValueChange={(v) => setEditForm({...editForm, ruta_cobro_id: v === 'none' ? '' : v})}
                                >
                                    <SelectTrigger data-testid="edit-select-ruta-cobro">
                                        <SelectValue placeholder="Selecciona una ruta de cobro" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sin ruta asignada</SelectItem>
                                        {rutas.map((r) => (
                                            <SelectItem key={r.id} value={r.id}>
                                                {r.nombre}{r.es_default ? ' (por defecto)' : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {rutas.length === 0 && (
                                    <p className="text-xs text-slate-400">
                                        No hay rutas configuradas. Crea una en{' '}
                                        <a href="/rutas-cobro" className="text-[#3D7A7A] underline">Rutas de Cobro</a>.
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center gap-3 pt-2 border-t">
                                <Checkbox
                                    id="edit-requiere-edp"
                                    checked={editForm.requiere_edp}
                                    onCheckedChange={(checked) => setEditForm({...editForm, requiere_edp: checked === true})}
                                    data-testid="edit-requiere-edp-checkbox"
                                />
                                <Label htmlFor="edit-requiere-edp" className="cursor-pointer text-sm">
                                    Este servicio requiere generación de EDP (Estado de Pago)
                                </Label>
                            </div>

                            {/* Extender mensualmente */}
                            {servicio?.periodo_mes && servicio?.periodo_anio && (
                                <div className="pt-4 border-t border-dashed border-slate-200" data-testid="extender-section">
                                    <div className="flex items-start gap-2 mb-3">
                                        <div className="w-9 h-9 rounded-lg bg-[#3D7A7A]/10 flex items-center justify-center flex-shrink-0">
                                            <Calendar size={16} className="text-[#3D7A7A]" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-slate-800">Extender mensualmente</p>
                                            <p className="text-xs text-slate-500 leading-relaxed">
                                                Replica este servicio mes a mes desde <span className="font-medium text-slate-700">
                                                {String(servicio.periodo_mes).padStart(2, '0')}/{servicio.periodo_anio}</span> hasta el período que indiques.
                                                {servicio?.grupo_recurrente_id
                                                    ? ' Los nuevos períodos se agregan al grupo recurrente existente.'
                                                    : ' Se creará un grupo recurrente con este servicio y las copias.'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                        <div className="space-y-1">
                                            <Label className="text-xs text-slate-500">Hasta el mes</Label>
                                            <select
                                                value={extendMes}
                                                onChange={(e) => setExtendMes(e.target.value)}
                                                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3D7A7A]/30"
                                                data-testid="extender-mes-select"
                                            >
                                                <option value="">Mes…</option>
                                                {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((nombre, idx) => (
                                                    <option key={idx + 1} value={idx + 1}>{nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-slate-500">Año</Label>
                                            <select
                                                value={extendAnio}
                                                onChange={(e) => setExtendAnio(e.target.value)}
                                                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3D7A7A]/30"
                                                data-testid="extender-anio-select"
                                            >
                                                <option value="">Año…</option>
                                                {(() => {
                                                    const base = servicio.periodo_anio || new Date().getFullYear();
                                                    return Array.from({ length: 6 }, (_, i) => base + i).map((y) => (
                                                        <option key={y} value={y}>{y}</option>
                                                    ));
                                                })()}
                                            </select>
                                        </div>
                                    </div>
                                    <Button
                                        type="button"
                                        onClick={handleExtenderMensual}
                                        disabled={extending || !extendMes || !extendAnio}
                                        variant="outline"
                                        className="w-full border-[#3D7A7A] text-[#3D7A7A] hover:bg-[#3D7A7A]/5"
                                        data-testid="extender-btn"
                                    >
                                        {extending ? (
                                            <><Loader2 size={14} className="mr-2 animate-spin" /> Extendiendo…</>
                                        ) : (
                                            <><Calendar size={14} className="mr-2" /> Crear períodos mensuales hasta…</>
                                        )}
                                    </Button>
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit">Guardar Cambios</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Editar Pago Dialog */}
            <Dialog open={editPagoDialogOpen} onOpenChange={setEditPagoDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Editar Pago</DialogTitle>
                    </DialogHeader>
                    {editPagoForm && (
                        <form onSubmit={handleEditPago}>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label>Fecha de Pago</Label>
                                    <Input
                                        type="date"
                                        value={editPagoForm.fecha_pago}
                                        onChange={(e) => setEditPagoForm({...editPagoForm, fecha_pago: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Monto</Label>
                                    <Input
                                        type="text"
                                        value={editPagoForm.monto ? `$${Math.round(editPagoForm.monto).toLocaleString('es-CL')}` : ''}
                                        onChange={(e) => {
                                            const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                            setEditPagoForm({...editPagoForm, monto: parseInt(numericValue) || 0});
                                        }}
                                        required
                                        placeholder="$0"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Método de Pago</Label>
                                    <Select 
                                        value={editPagoForm.metodo_pago} 
                                        onValueChange={(v) => setEditPagoForm({...editPagoForm, metodo_pago: v})}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {METODOS_PAGO.map((m) => (
                                                <SelectItem key={m.value} value={m.value}>
                                                    {m.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Observaciones</Label>
                                    <Textarea
                                        value={editPagoForm.observaciones || ''}
                                        onChange={(e) => setEditPagoForm({...editPagoForm, observaciones: e.target.value})}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setEditPagoDialogOpen(false)}>
                                    Cancelar
                                </Button>
                                <Button type="submit">Guardar Cambios</Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            {/* Eliminar Pago Dialog */}
            <Dialog open={deletePagoDialogOpen} onOpenChange={setDeletePagoDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Eliminar Pago</DialogTitle>
                        <DialogDescription>
                            ¿Está seguro que desea eliminar este pago? Esta acción no se puede deshacer.
                        </DialogDescription>
                    </DialogHeader>
                    {pagoToDelete && (
                        <div className="py-4">
                            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                                <p className="font-semibold text-rose-700">{formatCurrency(pagoToDelete.monto)}</p>
                                <p className="text-sm text-rose-600">{formatDate(pagoToDelete.fecha_pago)} • {pagoToDelete.metodo_pago}</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDeletePagoDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button 
                            type="button" 
                            variant="destructive"
                            onClick={handleDeletePago}
                            data-testid="confirm-delete-pago-btn"
                        >
                            Eliminar Pago
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Aplicar a Futuros Dialog */}
            <Dialog open={aplicarFuturosDialogOpen} onOpenChange={setAplicarFuturosDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>¿Aplicar cambios a períodos futuros?</DialogTitle>
                        <DialogDescription>
                            {servicio?.grupo_recurrente_id 
                                ? "Este servicio pertenece a un grupo de servicios recurrentes. ¿Desea aplicar los cambios también a los períodos futuros?"
                                : "Este servicio tiene un período asignado. ¿Cómo desea aplicar los cambios?"
                            }
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-3">
                        <div className="p-3 bg-slate-50 rounded-lg border">
                            <p className="font-medium text-sm">Solo este período</p>
                            <p className="text-xs text-slate-500">
                                Los cambios se aplicarán únicamente al período {servicio?.periodo_mes}/{servicio?.periodo_anio}
                            </p>
                        </div>
                        {servicio?.grupo_recurrente_id ? (
                            <div className="p-3 bg-[#E8F4F4] rounded-lg border border-[#A5D0D0]">
                                <p className="font-medium text-sm text-[#2D5A5A]">Este y períodos futuros</p>
                                <p className="text-xs text-[#5B9A9A]">
                                    Los cambios se aplicarán a este período y todos los siguientes del mismo grupo
                                </p>
                            </div>
                        ) : (
                            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                <p className="font-medium text-sm text-amber-800">Este y períodos futuros</p>
                                <p className="text-xs text-amber-600">
                                    Este servicio no pertenece a un grupo recurrente. Si desea aplicar cambios masivos, 
                                    primero debe crear servicios recurrentes desde la opción "Nuevo Servicio".
                                </p>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => {
                                setAplicarFuturosDialogOpen(false);
                                setPendingEditData(null);
                            }}
                            className="w-full sm:w-auto"
                        >
                            Cancelar
                        </Button>
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={handleAplicarSoloEste}
                            className="w-full sm:w-auto"
                        >
                            Solo este período
                        </Button>
                        {servicio?.grupo_recurrente_id && (
                            <Button 
                                type="button" 
                                onClick={handleAplicarConFuturos}
                                className="w-full sm:w-auto"
                            >
                                Este y futuros
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* EDP Management Dialog */}
            <Dialog open={edpDialogOpen} onOpenChange={setEdpDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText size={20} className="text-purple-600" />
                            Gestionar EDP
                        </DialogTitle>
                        <DialogDescription>
                            Período: {selectedEdp?.periodo_mes}/{selectedEdp?.periodo_anio}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedEdp && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <span className="text-sm font-medium">Estado actual:</span>
                                <Badge 
                                    variant="outline" 
                                    className={`${getEdpEstadoBadge(selectedEdp.estado)}`}
                                >
                                    {getEdpEstadoLabel(selectedEdp.estado)}
                                </Badge>
                            </div>
                            
                            {selectedEdp.fecha_respuesta && (
                                <div className="text-sm">
                                    <span className="text-slate-500">Fecha de respuesta: </span>
                                    <span className="font-medium">{formatDate(selectedEdp.fecha_respuesta)}</span>
                                </div>
                            )}
                            
                            {selectedEdp.numero_oc && (
                                <div className="text-sm">
                                    <span className="text-slate-500">Nº OC: </span>
                                    <span className="font-medium text-emerald-600">{selectedEdp.numero_oc}</span>
                                </div>
                            )}
                            
                            <div className="pt-4 border-t">
                                <p className="text-sm font-medium mb-3">Cambiar estado:</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleUpdateEdpEstado('enviado')}
                                        disabled={selectedEdp.estado === 'enviado'}
                                        className="bg-[#E8F4F4] text-[#3D7A7A] border-[#8BC5C5] hover:bg-[#D5E8E8] disabled:opacity-50"
                                    >
                                        Enviado
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleUpdateEdpEstado('rechazado')}
                                        disabled={selectedEdp.estado === 'rechazado'}
                                        className="bg-rose-50 text-rose-700 border-rose-300 hover:bg-rose-100 disabled:opacity-50"
                                    >
                                        Rechazado
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleUpdateEdpEstado('aprobado')}
                                        disabled={selectedEdp.estado === 'aprobado'}
                                        className="col-span-2 bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100 disabled:opacity-50"
                                    >
                                        Aprobado
                                    </Button>
                                </div>
                            </div>

                            {/* Delete EDP */}
                            <div className="pt-4 border-t">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setDeleteEdpDialogOpen(true)}
                                    className="w-full bg-rose-50 text-rose-700 border-rose-300 hover:bg-rose-100"
                                >
                                    <Trash2 size={14} className="mr-2" />
                                    Eliminar EDP
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEdpDialogOpen(false)}>
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete EDP Confirmation Dialog */}
            <Dialog open={deleteEdpDialogOpen} onOpenChange={setDeleteEdpDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Eliminar EDP</DialogTitle>
                        <DialogDescription>
                            ¿Está seguro que desea eliminar este EDP? Esta acción no se puede deshacer.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedEdp && (
                        <div className="py-4">
                            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                                <p className="font-semibold text-rose-700">EDP Nº {selectedEdp.numero_edp}</p>
                                <p className="text-sm text-rose-600">Período: {selectedEdp.periodo_mes}/{selectedEdp.periodo_anio}</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDeleteEdpDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button 
                            type="button" 
                            variant="destructive"
                            onClick={handleDeleteEdp}
                        >
                            Eliminar EDP
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create EDP Dialog */}
            <Dialog open={createEdpDialogOpen} onOpenChange={setCreateEdpDialogOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText size={20} className="text-purple-600" />
                            Crear EDP
                        </DialogTitle>
                        <DialogDescription>
                            Período: {servicio?.periodo_mes}/{servicio?.periodo_anio}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label htmlFor="nuevo_edp_numero">Número de EDP</Label>
                            <Input
                                id="nuevo_edp_numero"
                                type="number"
                                value={nuevoEdpNumero}
                                onChange={(e) => setNuevoEdpNumero(e.target.value)}
                                placeholder="Ingrese el número de EDP"
                                autoFocus
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateEdpDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handleCreateEdp}
                            disabled={!nuevoEdpNumero}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            Crear EDP
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Crear EDP Dialog */}
            <Dialog open={crearEdpOpen} onOpenChange={(open) => {
                setCrearEdpOpen(open);
                if (open && servicio) {
                    setEdpForm(prev => ({
                        ...prev,
                        faena: servicio.faena || '',
                        descripcion: servicio.nombre_producto || '',
                        comentarios: servicio.observaciones || '',
                        contacto_nombre: cliente?.contacto_financiero || '',
                        contacto_email: cliente?.email_cobranza || '',
                        contacto_telefono: cliente?.telefono || '',
                    }));
                }
                if (!open) setEdpForm({ descripcion: '', faena: '', contacto_nombre: '', contacto_email: '', contacto_telefono: '', comentarios: '', edp_secuencia: '', monto_neto: '' });
            }}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2" data-testid="crear-edp-modal-title">
                            <FileText className="text-[#3D7A7A]" size={20} />
                            Crear Estado de Pago (EDP)
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCrearEdp} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="text-sm font-medium text-slate-700">Descripción del servicio</label>
                                <input
                                    data-testid="edp-descripcion"
                                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5B9A9A] focus:border-transparent"
                                    placeholder={servicio?.nombre_producto || 'Descripción del servicio'}
                                    value={edpForm.descripcion}
                                    onChange={(e) => setEdpForm({...edpForm, descripcion: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-slate-700">Faena / Ubicación</label>
                                <input
                                    data-testid="edp-faena"
                                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5B9A9A] focus:border-transparent"
                                    placeholder="Ej: Oficina Central Santiago"
                                    value={edpForm.faena}
                                    onChange={(e) => setEdpForm({...edpForm, faena: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-slate-700">Secuencia EDP</label>
                                <input
                                    data-testid="edp-secuencia"
                                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5B9A9A] focus:border-transparent"
                                    placeholder="Ej: 2/31"
                                    value={edpForm.edp_secuencia}
                                    onChange={(e) => setEdpForm({...edpForm, edp_secuencia: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <p className="text-sm font-medium text-slate-700 mb-3">Contacto del cliente (se carga automáticamente desde la ficha del cliente)</p>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="text-xs text-slate-500">Nombre</label>
                                    <input
                                        data-testid="edp-contacto-nombre"
                                        className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5B9A9A] focus:border-transparent"
                                        value={edpForm.contacto_nombre}
                                        onChange={(e) => setEdpForm({...edpForm, contacto_nombre: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-500">Correo</label>
                                    <input
                                        data-testid="edp-contacto-email"
                                        className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5B9A9A] focus:border-transparent"
                                        value={edpForm.contacto_email}
                                        onChange={(e) => setEdpForm({...edpForm, contacto_email: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-500">Teléfono</label>
                                    <input
                                        data-testid="edp-contacto-telefono"
                                        className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5B9A9A] focus:border-transparent"
                                        value={edpForm.contacto_telefono}
                                        onChange={(e) => setEdpForm({...edpForm, contacto_telefono: e.target.value})}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <p className="text-sm font-medium text-slate-700 mb-3">Montos</p>
                            <div>
                                <label className="text-xs text-slate-500">Monto Neto (dejar vacío para usar el del servicio: {servicio?.monto_neto ? `$${parseFloat(servicio.monto_neto).toLocaleString('es-CL')}` : ''})</label>
                                <input
                                    data-testid="edp-monto-neto"
                                    type="number"
                                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5B9A9A] focus:border-transparent"
                                    placeholder={servicio?.monto_neto?.toString() || '0'}
                                    value={edpForm.monto_neto}
                                    onChange={(e) => setEdpForm({...edpForm, monto_neto: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <label className="text-sm font-medium text-slate-700">Comentarios / Observaciones</label>
                            <textarea
                                data-testid="edp-comentarios"
                                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5B9A9A] focus:border-transparent"
                                rows={3}
                                placeholder="Observaciones adicionales..."
                                value={edpForm.comentarios}
                                onChange={(e) => setEdpForm({...edpForm, comentarios: e.target.value})}
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                            <Button type="button" variant="outline" onClick={() => setCrearEdpOpen(false)}>
                                Cancelar
                            </Button>
                            <Button 
                                type="submit" 
                                disabled={crearEdpLoading}
                                className="bg-[#3D7A7A] hover:bg-[#2D5A5A]"
                                data-testid="crear-edp-submit"
                            >
                                {crearEdpLoading ? 'Generando...' : 'Crear EDP y Generar PDF'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Modal de advertencia: número de factura duplicado */}
            <Dialog
                open={duplicadoWarning.open}
                onOpenChange={(open) => {
                    if (!open) setDuplicadoWarning({ open: false, info: null, retryFn: null });
                }}
            >
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-700">
                            <AlertTriangle className="w-5 h-5" />
                            Número de factura repetido
                        </DialogTitle>
                        <DialogDescription className="text-slate-700 pt-2">
                            El número de factura{' '}
                            <strong className="text-slate-900">{`"${duplicadoWarning.info?.numero_factura || ''}"`}</strong>{' '}
                            ya está siendo usado por{' '}
                            <strong>
                                {duplicadoWarning.info?.total_existing || 1}{' '}
                                servicio{(duplicadoWarning.info?.total_existing || 1) !== 1 ? 's' : ''}
                            </strong>{' '}
                            en tu empresa.
                        </DialogDescription>
                    </DialogHeader>

                    {duplicadoWarning.info && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 max-h-64 overflow-y-auto">
                            <p className="font-medium text-amber-900 mb-2 text-sm">
                                {(duplicadoWarning.info?.total_existing || 1) === 1
                                    ? 'Servicio existente con este número:'
                                    : 'Servicios existentes con este número:'}
                            </p>
                            <ul className="space-y-2">
                                {(duplicadoWarning.info?.existing && duplicadoWarning.info.existing.length > 0
                                    ? duplicadoWarning.info.existing
                                    : [{
                                        cliente: duplicadoWarning.info?.existing_cliente,
                                        descripcion: duplicadoWarning.info?.existing_descripcion,
                                    }]
                                ).map((item, idx) => {
                                    const meses = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                                    const tienePeriodo = item.periodo_mes && item.periodo_anio;
                                    const periodoStr = tienePeriodo
                                        ? `${meses[item.periodo_mes] || item.periodo_mes}-${item.periodo_anio}`
                                        : '';
                                    return (
                                        <li key={item.servicio_id || idx} className="bg-white/60 rounded p-2 border border-amber-200 text-sm">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-amber-900 font-medium truncate">
                                                        {item.cliente || '—'}
                                                    </p>
                                                    {item.descripcion && (
                                                        <p className="text-amber-800 text-xs mt-0.5 truncate">
                                                            {item.descripcion}
                                                        </p>
                                                    )}
                                                </div>
                                                {tienePeriodo && (
                                                    <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 text-xs font-medium">
                                                        <Calendar className="w-3 h-3" />
                                                        {periodoStr}
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    <p className="text-sm text-slate-600">
                        ¿Deseas continuar de todas formas? Se permitirá tener varios servicios con el mismo número de factura.
                    </p>

                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setDuplicadoWarning({ open: false, info: null, retryFn: null })}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="button"
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={() => {
                                if (typeof duplicadoWarning.retryFn === 'function') {
                                    duplicadoWarning.retryFn();
                                }
                            }}
                        >
                            Continuar de todas formas
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Email preview modal (EDP + payment reminders) */}
            {emailPreviewCfg?.open && (
                <EmailPreviewModal
                    open={emailPreviewCfg.open}
                    onClose={() => setEmailPreviewCfg(null)}
                    title={emailPreviewCfg.title}
                    defaultTo={emailPreviewCfg.defaultTo}
                    defaultCc={emailPreviewCfg.defaultCc}
                    allowSchedule={emailPreviewCfg.allowSchedule}
                    fetchPreview={emailPreviewCfg.fetchPreview}
                    sendEmail={emailPreviewCfg.sendEmail}
                    scheduleEmail={emailPreviewCfg.scheduleEmail}
                    apiBaseUrl={API_URL}
                />
            )}
        </div>
    );
};

export default ServicioDetalle;
